/*
  Cart API (Stage 3 of the OS 2.0 migration, Jul 2026).
  Vanilla replacement for Shopify's deprecated api.jquery.js and the theme's
  cart.api.aio.min.js. The public surface is kept 1:1 so every existing call
  site keeps working: addItem, addItemFromForm, addItemFromFormStart, getCart,
  removeItem, onItemAdded, onCartUpdate, cartUpdateInfo, onError, cartPopap,
  cartPopapForm, formatMoney, fullMessagesFromErrors.

  Notes:
  - showCartToast (snippets/cart-toast.liquid) stays the success UI, as before; the
    /cart/add.js response is passed as its third argument so the modal targets that line.
  - The quickview branch (form id add-item-qv) is ported, quickview itself is dormant.
  - The error modal (#modalAddToCartError) is toggled through Bootstrap via jQuery
    when available (app.min.js still ships it), with a plain fallback otherwise.
  - The legacy shopping__cart drawer cleanup from the old onError was dead code
    (no such drawer in this theme) and was dropped.
*/
(function () {
  'use strict';

  var S = window.Shopify = window.Shopify || {};

  /* ---------------- helpers ---------------- */

  function thousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep || ',');
  }

  S.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    var fmt = format || '$' + '{' + '{amount}' + '}';
    var parts = (cents / 100).toFixed(2).split('.');
    var amount = thousands(parts[0]) + '.' + parts[1];
    var noDec = thousands(String(Math.round(cents / 100)));
    var comma = thousands(parts[0], '.') + ',' + parts[1];
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma);
  };

  S.fullMessagesFromErrors = function (errors) {
    var out = [];
    for (var key in errors) {
      if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
      var messages = errors[key];
      if (!Array.isArray(messages)) messages = [messages];
      for (var i = 0; i < messages.length; i++) {
        out.push(key === 'base' ? messages[i] : key + ' ' + messages[i]);
      }
    }
    return out;
  };

  function postJson(url, body, onOk, onErr) {
    fetch(url, {
      method: 'POST',
      body: body,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (res) {
      return res.json().then(function (data) {
        if (res.ok) { onOk(data); } else { onErr(data); }
      });
    }).catch(function () {
      onErr({ message: 'Network error', description: 'Network error. Please try again.' });
    });
  }

  /* ---------------- error handling ---------------- */

  /* Accepts either the parsed /cart/add.js error payload or a legacy
     XMLHttpRequest-like object with responseText, for compatibility. */
  S.onError = function (errOrXhr) {
    var data = errOrXhr || {};
    if (typeof data.responseText === 'string') {
      try { data = JSON.parse(data.responseText); } catch (e) { data = {}; }
    }
    var str = data.message
      ? (data.description || data.message)
      : 'Error : ' + S.fullMessagesFromErrors(data).join('; ') + '.';

    var msgEls = document.querySelectorAll('.error_message');
    for (var i = 0; i < msgEls.length; i++) msgEls[i].textContent = str;

    var modal = document.getElementById('modalAddToCartError');
    if (window.tpcModal) {
      window.tpcModal.show(modal);
    } else if (window.jQuery && window.jQuery.fn && window.jQuery.fn.modal) {
      window.jQuery(modal).modal('toggle');
    } else if (modal) {
      modal.classList.add('show');
      modal.style.display = 'block';
      modal.removeAttribute('aria-hidden');
    }
  };

  /* ---------------- cart reads / updates ---------------- */

  S.getCart = function (callback) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (typeof callback === 'function') { callback(cart); } else { S.onCartUpdate(cart); }
      })
      .catch(function () {});
  };

  S.onItemAdded = function (lineItem) {
    S.getCart();
  };

  S.onCartUpdate = function (cart) {
    S.cartUpdateInfo(cart, '.mini-cart-product');
  };

  var MINI_FMT = "<span class='money'>$" + '{' + '{amount}' + '}' + '</span>';

  S.cartUpdateInfo = function (cart, containerSel) {
    /* The header mini cart was removed from this theme; the container query is
       kept for compatibility and simply finds nothing. Counters still update. */
    var container = (typeof containerSel === 'string') ? document.querySelector(containerSel) : null;
    if (container && cart && cart.items) {
      container.innerHTML = '';
      var max = Math.min(cart.items.length, 20);
      for (var i = 0; i < max; i++) {
        var it = cart.items[i];
        var div = document.createElement('div');
        div.className = 'mini-product';
        div.innerHTML =
          '<div class="mini-product__thumb"><a href="' + it.url + '"><img src="' + (it.image || '') + '" alt=""></a></div>' +
          '<div class="mini-product__info"><h2 class="title"><a href="' + it.url + '">' + it.title + '</a></h2>' +
          '<div class="mini-calculation"><p class="price">' + it.quantity + ' x <span>' + S.formatMoney(it.price, MINI_FMT) + '</span></p>' +
          '<button class="remove-pro" onclick="Shopify.removeItem(' + it.variant_id + ')"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/></svg></button></div></div>';
        container.appendChild(div);
      }
    }

    var totals = document.querySelectorAll('.shopping-cart__total');
    for (var t = 0; t < totals.length; t++) totals[t].innerHTML = S.formatMoney(cart.total_price, MINI_FMT);

    if (typeof window.tpcUpdateCartCounters === 'function') {
      window.tpcUpdateCartCounters(cart);
    } else {
      var counters = document.querySelectorAll('.bigcounter');
      for (var c = 0; c < counters.length; c++) counters[c].textContent = cart.item_count;
    }
  };

  S.removeItem = function (variantId, callback) {
    postJson('/cart/change.js', new URLSearchParams({ quantity: 0, id: variantId }),
      function (cart) {
        if (typeof callback === 'function') { callback(cart); } else { S.onCartUpdate(cart); }
      },
      S.onError);
  };

  /* ---------------- add to cart ---------------- */

  S.addItem = function (variantId, quantity, callback) {
    quantity = quantity || 1;
    postJson('/cart/add.js', new URLSearchParams({ quantity: quantity, id: variantId }),
      function (item) {
        if (typeof callback === 'function') {
          callback(item);
        } else {
          if (typeof window.showCartToast === 'function') {
            window.showCartToast(item.product_title, item.image, item);
          } else {
            S.cartPopap(variantId);
          }
          S.onItemAdded(item);
        }
      },
      S.onError);
  };


  S.addItemFromForm = function (formId, productId, callback) {
    var form = document.getElementById(formId);
    if (!form) return;

    postJson('/cart/add.js', new FormData(form),
      function (item) {
        if (typeof callback === 'function') {
          callback(item);
        } else {
          if (typeof window.showCartToast === 'function') {
            window.showCartToast(item.product_title, item.image, item);
          } else {
            S.cartPopapForm(productId);
          }
        }
        S.onItemAdded(item);
      },
      S.onError);
  };

  S.addItemFromFormStart = function (formId, productId) {
    S.addItemFromForm(formId, productId);
  };

  /* ---------------- legacy popup fallbacks ---------------- */

  S.cartPopap = function (productClass) {
    var img = document.querySelector('.' + productClass + ' .popup_cart_image');
    var ttl = document.querySelector('.' + productClass + ' .popup_cart_title');
    var src = img ? img.getAttribute('src') : '';
    var title = ttl ? ttl.textContent : '';
    if (typeof window.showCartToast === 'function') {
      window.showCartToast(title, src);
    } else {
      var pi = document.querySelector('.popupimage');
      var pm = document.querySelector('.productmsg');
      if (pi) pi.setAttribute('src', src);
      if (pm) pm.textContent = title;
      if (window.tpcModal) { window.tpcModal.toggle('modalAddToCart'); }
    }
  };

  S.cartPopapForm = function (productId) {
    var img = document.querySelector('.product_variant_image');
    var ttl = document.getElementById('popup_cart_title');
    var src = img ? img.getAttribute('src') : '';
    var title = ttl ? ttl.textContent : '';
    if (typeof window.showCartToast === 'function') {
      window.showCartToast(title, src);
    } else {
      var pi = document.querySelector('.popupimage');
      var pm = document.querySelector('.productmsg');
      if (pi) pi.setAttribute('src', src);
      if (pm) pm.textContent = title;
      if (window.tpcModal) { window.tpcModal.toggle('modalAddToCart'); }
    }
  };
})();
